import { useEffect } from 'react';
import { type FieldValues, type Path } from 'react-hook-form';
import { PHONE_INPUT_DEBOUNCE_TIME } from '@/config';
import {
  createPhoneHandlers,
  type CreatePhoneHandlersParams,
} from '@/utils/phoneValidationHandler';

type UsePhoneFieldValidationParams<
  T extends FieldValues,
  TFieldName extends Path<T> = Path<T>,
> = Omit<CreatePhoneHandlersParams<T, TFieldName>, 'debounceTime'> & {
  debounceTime?: number;
};

const usePhoneFieldValidation = <
  T extends FieldValues,
  TFieldName extends Path<T> = Path<T>,
>({
  fieldName,
  checkPhoneNumber,
  clearErrors,
  setError,
  setValue,
  errorMessage,
  debounceTime = PHONE_INPUT_DEBOUNCE_TIME,
}: UsePhoneFieldValidationParams<T, TFieldName>) => {
  const handlers = createPhoneHandlers<T, TFieldName>({
    fieldName,
    checkPhoneNumber,
    clearErrors,
    setError,
    setValue,
    errorMessage,
    debounceTime,
  });

  useEffect(() => {
    return () => {
      handlers.debouncedCheckPhone.cancel();
    };
  }, [handlers.debouncedCheckPhone]);

  return handlers;
};

export default usePhoneFieldValidation;
